import { CircleBranding, CircleBrandingButtonShape } from 'src/api/circles';

/**
 * Dunbar defaults. These must equal the values hard-coded across the app so
 * that an unbranded circle renders exactly as it did before branding existed.
 */
export const DUNBAR_BRAND = {
  displayName: 'Dunbar148',
  logoUrl: undefined as string | undefined,
  logoDarkUrl: undefined as string | undefined,
  primaryColor: '#f0544f',
  secondaryColor: '#ebebf0',
  accentColor: '#f0544f',
  backgroundColor: '#ffffff',
  textColor: '#191920',
  fontFamily: 'TikTokFont, Arial, Tahoma, PingFangSC, sans-serif',
};

export type ResolvedBranding = {
  isBranded: boolean;
  /** True when the circle set its own display_name. */
  hasDisplayName: boolean;
  displayName: string;
  logoUrl?: string;
  logoDarkUrl?: string;

  /**
   * True when the circle set its own primary_color. The top bar, the circle
   * header and the tab bar then form one brand-colored "header zone".
   */
  hasHeaderSurface: boolean;
  primaryColor: string;
  /** Text and icons on top of primaryColor. */
  onPrimaryColor: string;

  secondaryColor: string;
  /** True when the circle set a color that can drive buttons and links. */
  hasAccent: boolean;
  /** Buttons, links and the active tab. secondary_color when set, else primary. */
  accentColor: string;
  accentHoverColor: string;
  /** Accent that stays readable as link text on a light surface. */
  accentTextColor: string;
  /** Text on top of accentColor. */
  onAccentColor: string;

  /** True when the circle set its own background_color. */
  hasPageSurface: boolean;
  backgroundColor: string;
  /** Body text on top of backgroundColor. */
  textColor: string;

  /** True when the circle set its own font_family. */
  hasFont: boolean;
  fontFamily: string;
  /** True when headings have a brand font (their own, or the body font). */
  hasHeadingFont: boolean;
  headingFontFamily: string;
  headingUppercase: boolean;
  buttonShape: CircleBrandingButtonShape;
  /**
   * True when the circle asked for the app navigation (sidebar, circle strip)
   * to take its header color while it is open. Needs a header color.
   */
  brandNavigation: boolean;
};

const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const HTTPS_URL_REGEX = /^https:\/\/\S+$/;
// Same rule as the API: plain font-name characters only
const FONT_FAMILY_REGEX = /^[\w\s,'"-]+$/;

export const isHexColor = (value: unknown): value is string =>
  typeof value === 'string' && HEX_COLOR_REGEX.test(value.trim());

export const isHttpsUrl = (value: unknown): value is string =>
  typeof value === 'string' && HTTPS_URL_REGEX.test(value.trim());

export const isFontFamily = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.trim().length > 0 &&
  value.trim().length <= 120 &&
  FONT_FAMILY_REGEX.test(value.trim());

const cleanString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const toRgb = (hex: string): [number, number, number] | null => {
  const normalized = hex.trim().replace('#', '');
  const full =
    normalized.length === 3
      ? normalized
          .split('')
          .map((char) => char + char)
          .join('')
      : normalized;
  const rgb: [number, number, number] = [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
  return rgb.some((n) => Number.isNaN(n)) ? null : rgb;
};

/** Picks black or white text for a given background color. */
export const contrastTextColor = (hex: string): '#ffffff' | '#191920' => {
  const rgb = toRgb(hex);
  if (!rgb) {
    return '#191920';
  }
  const [r, g, b] = rgb;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#191920' : '#ffffff';
};

/** `rgba()` string for a hex color; used for muted text and hairlines. */
export const withAlpha = (hex: string, alpha: number): string => {
  const rgb = toRgb(hex);
  if (!rgb) {
    return hex;
  }
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
};

/** WCAG contrast ratio between two hex colors (1 to 21). */
export const contrastRatio = (a: string, b: string): number => {
  const luminance = (hex: string): number => {
    const rgb = toRgb(hex);
    if (!rgb) {
      return 0;
    }
    const [r, g, bl] = rgb.map((value) => {
      const c = value / 255;
      return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * bl;
  };
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

/**
 * A version of `color` that is readable as text on `background`: the color is
 * darkened step by step until it reaches the wanted contrast. Bright brand
 * accents (orange, yellow) work as button fills but not as link text.
 */
export const readableOn = (
  color: string,
  background: string,
  minRatio = 4.5
): string => {
  let candidate = color;
  for (let step = 0; step < 12; step += 1) {
    if (contrastRatio(candidate, background) >= minRatio) {
      return candidate;
    }
    candidate = darken(candidate, 0.08);
  }
  return candidate;
};

/** Lightens a hex color by mixing it with white; used for card surfaces. */
export const lighten = (hex: string, amount: number): string => {
  const rgb = toRgb(hex);
  if (!rgb) {
    return hex;
  }
  const channel = (value: number) =>
    Math.max(0, Math.min(255, Math.round(value + (255 - value) * amount)))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(rgb[0])}${channel(rgb[1])}${channel(rgb[2])}`;
};

/** Darkens a hex color by mixing it with black; used for hover states. */
export const darken = (hex: string, amount: number): string => {
  const rgb = toRgb(hex);
  if (!rgb) {
    return hex;
  }
  const channel = (value: number) =>
    Math.max(0, Math.min(255, Math.round(value * (1 - amount))))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(rgb[0])}${channel(rgb[1])}${channel(rgb[2])}`;
};

/**
 * True when the branding object carries at least one usable value. Empty
 * objects, blank strings and `false` flags count as "not branded".
 */
export const hasBranding = (
  branding?: CircleBranding | null
): branding is CircleBranding =>
  Boolean(
    branding &&
    Object.values(branding).some(
      (value) =>
        value === true || (typeof value === 'string' && value.trim().length > 0)
    )
  );

const UNBRANDED: ResolvedBranding = {
  isBranded: false,
  hasDisplayName: false,
  displayName: DUNBAR_BRAND.displayName,
  logoUrl: undefined,
  logoDarkUrl: undefined,
  hasHeaderSurface: false,
  primaryColor: DUNBAR_BRAND.primaryColor,
  onPrimaryColor: '#ffffff',
  secondaryColor: DUNBAR_BRAND.secondaryColor,
  hasAccent: false,
  accentColor: DUNBAR_BRAND.accentColor,
  accentHoverColor: '#c04440',
  accentTextColor: DUNBAR_BRAND.accentColor,
  onAccentColor: '#ffffff',
  hasPageSurface: false,
  backgroundColor: DUNBAR_BRAND.backgroundColor,
  textColor: DUNBAR_BRAND.textColor,
  hasFont: false,
  fontFamily: DUNBAR_BRAND.fontFamily,
  hasHeadingFont: false,
  headingFontFamily: DUNBAR_BRAND.fontFamily,
  headingUppercase: false,
  buttonShape: 'rounded',
  brandNavigation: false,
};

/**
 * Merges circle branding over the Dunbar defaults. Invalid colors, fonts and
 * non-https URLs are ignored field by field so a bad value can never break
 * the page. Each `has*` flag says whether the circle really set that aspect;
 * aspects that were not set keep the Dunbar look.
 */
export const resolveBranding = (
  branding?: CircleBranding | null
): ResolvedBranding => {
  if (!hasBranding(branding)) {
    return UNBRANDED;
  }

  const color = (value: unknown): string | undefined =>
    isHexColor(value) ? value.trim().toLowerCase() : undefined;
  const url = (value: unknown): string | undefined =>
    isHttpsUrl(value) ? value.trim() : undefined;
  const font = (value: unknown): string | undefined =>
    isFontFamily(value) ? value.trim() : undefined;

  const primary = color(branding.primary_color);
  const secondary = color(branding.secondary_color);
  const background = color(branding.background_color);
  const accent = secondary ?? primary;
  const bodyFont = font(branding.font_family);
  const headingFont = font(branding.heading_font_family);

  const primaryColor = primary ?? UNBRANDED.primaryColor;
  const accentColor = accent ?? UNBRANDED.accentColor;
  const backgroundColor = background ?? UNBRANDED.backgroundColor;

  const displayName = cleanString(branding.display_name);

  return {
    isBranded: true,
    hasDisplayName: Boolean(displayName),
    displayName: displayName ?? UNBRANDED.displayName,
    logoUrl: url(branding.logo_url),
    logoDarkUrl: url(branding.logo_dark_url),

    hasHeaderSurface: Boolean(primary),
    primaryColor,
    onPrimaryColor:
      color(branding.on_primary_color) ?? contrastTextColor(primaryColor),

    secondaryColor: secondary ?? UNBRANDED.secondaryColor,
    hasAccent: Boolean(accent),
    accentColor,
    accentHoverColor: accent
      ? darken(accentColor, 0.12)
      : UNBRANDED.accentHoverColor,
    // Links sit on white cards and directly on the page; the page is the
    // darker of the two when it is tinted, so it sets the bar.
    accentTextColor: accent
      ? readableOn(
          accentColor,
          contrastTextColor(backgroundColor) === '#ffffff'
            ? '#ffffff'
            : backgroundColor
        )
      : UNBRANDED.accentTextColor,
    onAccentColor: contrastTextColor(accentColor),

    hasPageSurface: Boolean(background),
    backgroundColor,
    textColor:
      color(branding.text_color) ??
      (background ? contrastTextColor(backgroundColor) : UNBRANDED.textColor),

    hasFont: Boolean(bodyFont),
    fontFamily: bodyFont ?? UNBRANDED.fontFamily,
    hasHeadingFont: Boolean(headingFont ?? bodyFont),
    headingFontFamily: headingFont ?? bodyFont ?? UNBRANDED.fontFamily,
    headingUppercase: branding.heading_uppercase === true,
    buttonShape: branding.button_shape === 'pill' ? 'pill' : 'rounded',
    brandNavigation: branding.brand_navigation === true && Boolean(primary),
  };
};

/**
 * CSS custom properties applied on the circle layout wrapper only. Every
 * consumer uses `var(--brand-x, <dunbar literal>)`, so outside a branded
 * scope the literal fallback keeps today's rendering untouched. A variable is
 * only emitted for an aspect the circle really set, so a circle that only has
 * a logo keeps every Dunbar color, font and shape.
 */
export const brandingToCssVars = (
  resolved: ResolvedBranding
): Record<string, string> => {
  const vars: Record<string, string> = {};
  if (!resolved.isBranded) {
    return vars;
  }

  if (resolved.hasAccent) {
    vars['--brand-accent'] = resolved.accentColor;
    vars['--brand-accent-hover'] = resolved.accentHoverColor;
    vars['--brand-accent-soft'] = withAlpha(resolved.accentColor, 0.1);
    vars['--brand-accent-muted'] = withAlpha(resolved.accentColor, 0.4);
    vars['--brand-accent-text'] = resolved.accentTextColor;
    vars['--brand-on-accent'] = resolved.onAccentColor;
  }

  if (resolved.hasHeaderSurface) {
    // The header color doubles as the quiet accent for labels and icons on
    // the page, so the accent color stays reserved for actions
    vars['--brand-primary-text'] = readableOn(
      resolved.primaryColor,
      contrastTextColor(resolved.backgroundColor) === '#ffffff'
        ? '#ffffff'
        : resolved.backgroundColor
    );
    vars['--brand-header-bg'] = resolved.primaryColor;
    vars['--brand-header-text'] = resolved.onPrimaryColor;
    vars['--brand-header-muted'] = withAlpha(resolved.onPrimaryColor, 0.72);
    vars['--brand-header-border'] = withAlpha(resolved.onPrimaryColor, 0.4);
    vars['--brand-header-hover'] = withAlpha(resolved.onPrimaryColor, 0.12);
  }

  if (resolved.hasPageSurface) {
    vars['--brand-background'] = resolved.backgroundColor;
    vars['--brand-text'] = resolved.textColor;
    vars['--brand-text-muted'] = withAlpha(resolved.textColor, 0.72);
    // Wide tables stay inside their white card instead of spilling onto the
    // tinted page
    vars['--brand-table-overflow'] = 'auto';
    // Surfaces for cards and form fields on the tinted page, built the way
    // brand websites usually do it: cards a little lighter than the page,
    // wells a little darker, hairlines in the text color. On a dark page the
    // steps are smaller so the light brand text stays readable on all of them.
    const darkPage = contrastTextColor(resolved.backgroundColor) === '#ffffff';
    vars['--brand-surface'] = lighten(
      resolved.backgroundColor,
      darkPage ? 0.07 : 0.6
    );
    vars['--brand-surface-sunken'] = darken(
      resolved.backgroundColor,
      darkPage ? 0.25 : 0.05
    );
    vars['--brand-field'] = darkPage
      ? lighten(resolved.backgroundColor, 0.12)
      : '#ffffff';
    vars['--brand-border'] = withAlpha(resolved.textColor, 0.14);
    vars['--brand-input-border'] = withAlpha(resolved.textColor, 0.26);
  }

  if (resolved.hasFont) {
    vars['--brand-font'] = resolved.fontFamily;
  }
  if (resolved.hasHeadingFont) {
    vars['--brand-heading-font'] = resolved.headingFontFamily;
  }
  if (resolved.headingUppercase) {
    vars['--brand-heading-transform'] = 'uppercase';
  }
  if (resolved.buttonShape === 'pill') {
    vars['--brand-button-radius'] = '9999px';
    // Soft, generous corners go with pill buttons
    vars['--brand-card-radius'] = '20px';
    vars['--brand-inner-radius'] = '12px';
  }

  return vars;
};

/** Class the navigation containers get when they follow a circle's brand. */
export const BRAND_NAVIGATION_CLASS = 'circle-brand-nav';

/**
 * CSS custom properties for the app navigation (sidebar, circle strip). Empty
 * unless the open circle asked for a branded navigation, so the navigation
 * stays Dunbar by default. Consumers only read these under
 * `.circle-brand-nav`, a class that is added together with the variables.
 * Meant to be spread into a `style` prop: besides the variables it carries the
 * circle font, which the navigation labels inherit.
 */
export const navigationCssVars = (
  resolved: ResolvedBranding
): Record<string, string> => {
  if (!resolved.brandNavigation) {
    return {};
  }
  // The accent marks the open circle if it stands out on the header color
  const active =
    contrastRatio(resolved.accentColor, resolved.primaryColor) >= 3
      ? resolved.accentColor
      : resolved.onPrimaryColor;
  return {
    '--nav-bg': resolved.primaryColor,
    '--nav-text': resolved.onPrimaryColor,
    '--nav-muted': withAlpha(resolved.onPrimaryColor, 0.72),
    '--nav-active': active,
    '--nav-hover': withAlpha(resolved.onPrimaryColor, 0.08),
    '--nav-border': withAlpha(resolved.onPrimaryColor, 0.16),
    '--nav-chip': withAlpha(resolved.onPrimaryColor, 0.14),
    // the navigation speaks the circle's typeface too
    ...(resolved.hasFont && { fontFamily: resolved.fontFamily }),
  };
};
